/**
 * TranslatableText — renders a message body, with an inline
 * "Translate" / "Show original" toggle. When the viewer has
 * `auto_translate_incoming` on, foreign-looking text translates
 * on first render. Cache lives server-side in message_translations.
 */
import { useEffect, useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import { useTranslate } from "@/lib/i18n/useTranslate";
import { useUserLocale } from "@/lib/i18n/useUserLocale";

interface Props {
  text: string;
  /** When true (and viewer is not the sender), auto-runs translation on mount. */
  autoTranslate?: boolean;
  className?: string;
}

const TranslatableText = ({ text, autoTranslate, className }: Props) => {
  const { locale } = useUserLocale();
  const { translate, translating } = useTranslate();
  const [translated, setTranslated] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!autoTranslate || tried || !text?.trim()) return;
    setTried(true);
    void (async () => {
      const out = await translate(text, locale);
      if (out.text && out.text.trim() !== text.trim()) setTranslated(out.text);
    })();
  }, [autoTranslate, tried, text, locale, translate]);

  const onToggle = async () => {
    if (translated) {
      setShowOriginal((v) => !v);
      return;
    }
    const out = await translate(text, locale);
    if (out.text) {
      setTranslated(out.text);
      setShowOriginal(false);
    }
  };

  const display = translated && !showOriginal ? translated : text;

  return (
    <div className={className}>
      <p className="whitespace-pre-wrap">{display}</p>
      <button
        type="button"
        onClick={onToggle}
        disabled={translating}
        className="mt-1 inline-flex items-center gap-1 text-[10px] underline-offset-2 hover:underline opacity-70 disabled:opacity-40"
      >
        {translating ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Languages className="h-3 w-3" />
        )}
        {translated
          ? showOriginal
            ? "Show translation"
            : "Show original"
          : "Translate"}
      </button>
    </div>
  );
};

export default TranslatableText;
